import {Component} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {BucketName, OrganizationApiService, OrganizationResponse, OrganizationType} from '@npck/af-frontend-api';
import {BIN_PATTERN, MAX_LENGTH, RouterService, rxSpinner, Spinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {concat, defaultIfEmpty, last, map, Observable, switchMap} from 'rxjs';
import {AttachmentForm} from '../../../../components/files/file/file.component';
import {ORGANIZATION_MIME_TYPES} from '../../../../domain/constants';
import {BIN_REFERENCES, DictionaryHolderService} from '../../../../domain/dictionary-holder.service';
import {getValidPhoneNumberOrNull, PhoneInfo} from '../../../../domain/phone-utils';
import {ORGANIZATION_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {AdminPanelTab} from '../../admin.component';

export const ORGANIZATION_SCOPE = 'ORGANIZATION';
export const ORGANIZATION_DOCUMENT_DELETION_SCOPE = 'ORGANIZATION_DOCUMENT';

@Component({
    templateUrl: 'organization-create-edit.component.html',
    standalone: false,
})
export class OrganizationCreateEditComponent {
    Spinner = Spinner;
    Path = Path;
    BucketName = BucketName;
    ORGANIZATION_MIME_TYPES = ORGANIZATION_MIME_TYPES;
    organizationScope = [ORGANIZATION_SCOPE];
    organizationTypes = Object.values(OrganizationType);
    organizationId: string;
    organization?: OrganizationResponse;
    form: FormGroup<OrganizationForm>;
    tabPathToTab: Record<string, AdminPanelTab> = {[Path.ORGANIZATIONS]: 'tabOrganizations'};

    constructor(
        private formBuilder: FormBuilder,
        private organizationApiService: OrganizationApiService,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private snackbarService: SnackbarService,
        private dictionaryHolderService: DictionaryHolderService,
    ) {
        this.organizationId = this.activatedRoute.snapshot.paramMap.get(ORGANIZATION_ID);
        this.form = this.buildForm();
        if (this.organizationId) {
            Spinner.show('initial');
            this.organizationApiService.getOrganization(this.organizationId)
                .pipe(rxSpinner('initial'))
                .subscribe(organization => {
                    this.organization = organization;
                    this.patchForm(organization);
                });
        }
    }

    filterOrganizationIdPath = (path: string): boolean => path === this.organizationId;

    navigateBack(): void {
        this.routerService.navigateToTab<AdminPanelTab>(Path.ADMIN_PANEL, 'tabOrganizations');
    }

    submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const request = this.organizationId ? this.updateOrganization() : this.createOrganization();
        request
            .pipe(rxSpinner('submit'))
            .subscribe(organization => {
                this.dictionaryHolderService.clearReferences(BIN_REFERENCES);
                this.routerService.navigateToTab<AdminPanelTab>(
                    `${Path.ADMIN_PANEL_ORGANIZATIONS}/${organization.id}`, 'tabOrganizations');
                this.snackbarService.showSnackBar(
                    this.organizationId ? 'i18n_af_snackbarMessageOrganizationUpdated' : 'i18n_af_snackbarMessageOrganizationCreated',
                    'success',
                    false,
                    {organizationName: organization.nameRu},
                );
            });
    }

    private createOrganization(): Observable<OrganizationResponse> {
        return this.organizationApiService.createOrganization({
            bin: this.form.controls.bin.getRawValue(),
            type: this.form.value.type,
            nameRu: this.form.value.nameRu,
            nameKz: this.form.value.nameKz,
            nameEn: this.form.value.nameEn,
            email: this.form.value.email,
            phone: this.getPhoneNumber(),
        }).pipe(switchMap(organization => this.saveDocuments(organization)));
    }

    private updateOrganization(): Observable<OrganizationResponse> {
        return this.organizationApiService.updateOrganization(this.organizationId, {
            type: this.form.value.type,
            nameRu: this.form.value.nameRu,
            nameKz: this.form.value.nameKz,
            nameEn: this.form.value.nameEn,
            email: this.form.value.email,
            phone: this.getPhoneNumber(),
        });
    }

    private saveDocuments(organization: OrganizationResponse): Observable<OrganizationResponse> {
        const requests = (this.form.controls.attachments?.getRawValue() || [])
            .filter(attachment => !!attachment.fileType && !!attachment.files?.length)
            .flatMap(attachment => (attachment.files || [])
                .filter(file => !file.isDeleted && !!file.id)
                .map(file => this.organizationApiService.createOrganizationDocument(organization.id, {
                    type: attachment.fileType,
                    fileId: file.id,
                })));
        return concat(...requests).pipe(
            defaultIfEmpty(null),
            last(),
            map(() => organization),
        );
    }

    private getPhoneNumber(): string {
        return getValidPhoneNumberOrNull(this.form.value.phone, this.form.value.selectedPhoneInfo);
    }

    private buildForm(): FormGroup<OrganizationForm> {
        return new FormGroup<OrganizationForm>({
            bin: new FormControl({value: null, disabled: !!this.organizationId},
                [Validators.required, Validators.pattern(BIN_PATTERN)]),
            nameRu: new FormControl(null, [Validators.required, Validators.maxLength(MAX_LENGTH)]),
            nameKz: new FormControl(null, [Validators.maxLength(MAX_LENGTH)]),
            nameEn: new FormControl(null, [Validators.maxLength(MAX_LENGTH)]),
            type: new FormControl(null, [Validators.required]),
            email: new FormControl(null, [Validators.required, Validators.email]),
            phone: new FormControl(null, [Validators.required]),
            selectedPhoneInfo: new FormControl(null),
            attachments: this.formBuilder.array([] as FormGroup<AttachmentForm>[]),
        });
    }

    private patchForm(organization: OrganizationResponse): void {
        this.form.patchValue({
            bin: organization.bin,
            nameRu: organization.nameRu,
            nameKz: organization.nameKz,
            nameEn: organization.nameEn,
            type: organization.type,
            email: organization.email,
            phone: organization.phone,
        });
    }
}

export interface OrganizationForm {
    bin?: FormControl<string | null>;
    nameRu?: FormControl<string | null>;
    nameKz?: FormControl<string | null>;
    nameEn?: FormControl<string | null>;
    type?: FormControl<OrganizationType | null>;
    email?: FormControl<string | null>;
    phone?: FormControl<string | null>;
    selectedPhoneInfo?: FormControl<PhoneInfo | null>;
    attachments?: FormArray<FormGroup<AttachmentForm>>;
}
